/**
 * TAM Declarations React Hooks
 * Epic 71: Futáros Kézbesítés & TAM
 *
 * TanStack Query hooks for TAM declaration CRUD and invoice validation.
 */

import {
  createTamDeclaration,
  getTamDeclaration,
  listTamDeclarations,
  validateTamInvoice,
  type CreateTamDeclarationInput,
  type TamDeclaration,
  type TamValidationResult,
  type ValidateTamInvoiceInput,
} from '@/api/tam';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useTamDeclarations() {
  const query = useQuery({
    queryKey: ['tam', 'declarations', 'list'],
    queryFn: async () => {
      const res = await listTamDeclarations();
      return res.data;
    },
  });

  return {
    declarations: (query.data ?? []) as TamDeclaration[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useTamDeclaration(id: string | undefined) {
  const query = useQuery({
    queryKey: ['tam', 'declarations', 'detail', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID');
      const res = await getTamDeclaration(id);
      return res.data;
    },
    enabled: !!id,
  });

  return {
    declaration: query.data as TamDeclaration | undefined,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useCreateTamDeclaration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTamDeclarationInput) => createTamDeclaration(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tam'] });
    },
  });
}

export function useValidateTamInvoice() {
  return useMutation<{ data: TamValidationResult }, Error, ValidateTamInvoiceInput>({
    mutationFn: (input: ValidateTamInvoiceInput) => validateTamInvoice(input),
  });
}
